import { ButtonDisableIdentifier } from './validator-identifier';
import { Action } from '@ngrx/store';
import { FieldItem } from './field-models';

export enum RowItemType {
    UI = 'ui',
    Section = 'section',
    Action = 'action',
}
/**
 * Base interface for all row items
 */
export interface RowItem {
    id: string;
    type: RowItemType;
}

export enum UIElementType {
    Button = 'button',
    Image = 'image',
    Icon = 'icon',
    Text = 'text',
    Divider = 'divider',
    Menu = 'menu',
    Component = 'component',
}

/**
 * UI item (for buttons, icons, text, dividers, etc)
 */
export interface UIItem extends RowItem {
    type: RowItemType.UI;
    uiType: UIElementType;
    name?: string;
    visible?: boolean;
    label?: string; // Used for text content in text UI type
    textClass?: string; // CSS class for text styling
    disabled?: boolean;
    iconName?: string;
    imgSrc?: string;
    imgAlt?: string;
    imgHeight?: number;
    imgWidth?: number;
    imgClass?: string;
    enabledImgSrc?: string;
    enabledImgClass?: string;
    tooltip?: string;
    panelClass?: string;
    dividerStyle?: 'solid' | 'dashed' | 'dotted'; // Style for divider UI type
    dividerColor?: string; // Color for divider UI type
    dividerThickness?: number; // Thickness for divider UI type
    
    events?: {
        [key: string]: (value: Event) => Action[];
    };

    disabledSelector?: ButtonDisableIdentifier;
    suffixText?: string;
    suffixTextClass?: string;
    menuConfig?: MenuConfig;
    componentId?: string;
}

/**
 * Section item (for nested sections)
 */
export interface SectionItem extends RowItem {
    type: RowItemType.Section;
    section: Section;
}

export interface ActionsSection extends Section {
    info: SectionInfo & {
        type: SectionType.Actions;
    };
}

export interface ActionConfig extends RowItem {
    type: RowItemType.Action;
    label: string;
    tooltip?: string;
    position: 'left' | 'right' | 'center';
    icon?: string;
    disabled?: boolean;
    visible?: boolean;
    events: {
        onClick: () => Action[];
    };
}

/**
 * Row information
 */
export interface RowInfo {
    id: string;
    panelClass?: string;
    tooltip?: string;
}

/**
 * Row definition
 */
export interface Row {
    info: RowInfo;
    items: (FieldItem | UIItem | SectionItem)[];
}

/**
 * Section types
 */
export enum SectionType {
    Standard = 'standard',
    Array = 'array',
    Actions = 'actions',
}

/**
 * Section information
 */
export interface SectionInfo {
    id: string;
    title: string;
    displayTitle: boolean;
    displayActionBtns?: boolean;
    type?: SectionType;
    panelClass?: string;
    expandable?: boolean;
    expanded?: boolean;
    metadata?: { [key: string]: unknown };
    errors?: { [key: string]: string };
    preserveOldValues?: boolean;
    isDynamic?: boolean;

    itemGenerator?: (index: number) => Row;
    maxItems?: number;
}

/**
 * Section definition (can be a top-level section or a nested subsection)
 */
export interface Section {
    info: SectionInfo;
    rows: Row[];
}

/**
 * Form information
 */
export interface FormInfo {
    id: string;
    title: string;
}

/**
 * Complete form configuration
 */
export interface FormConfig {
    info: FormInfo;
    sections: Section[];
}

/**
 * Form state including metadata
 */
export interface FormState {
    config: FormConfig;
    pristine?: boolean;
    valid?: boolean;
    submitted?: boolean;
    loading?: boolean;
}

export interface MenuConfig {
    icon?: string;
    label: string;
    items: (FieldItem | UIItem | SectionItem)[];
}
